/**
 * handlers/proactive.ts
 * Proactive suggestion loading, dismissal, and action routing.
 */

import { state } from '../state';
import { dom } from '../dom';
import {
  getProactiveSuggestions,
  dismissSuggestion as apiDismissSuggestion,
  getTodayBriefing as apiGetTodayBriefing,
} from '../api';
import {
  appendMessage, appendTypingIndicator, removeTypingIndicator,
  renderProactiveSuggestions,
} from '../ui-renderers';
import { showChat } from './views';
import { handleSend } from './chat';

export function loadProactiveSuggestions() {
  if (typeof AkiliPortal === 'undefined' || !AkiliPortal.apiFetch) return;
  getProactiveSuggestions()
    .then(data => renderProactiveSuggestions(data.suggestions || []))
    .catch(() => {});
}

export function dismissProactiveSuggestion(id: string) {
  if (!id) return;
  state.psDismissed[id] = true;

  const card = document.querySelector('.ps-card[data-ps-id="' + CSS.escape(id) + '"]') as HTMLElement | null;
  if (card) {
    card.style.transition = 'opacity .2s';
    card.style.opacity    = '0';
    setTimeout(() => {
      if (card.parentNode) card.parentNode.removeChild(card);
      state.psCount = Math.max(0, state.psCount - 1);
      if (state.psCount === 0) {
        const panel = dom.proactivePanel;
        const pill  = dom.psPill;
        if (panel) panel.classList.remove('has-suggestions');
        if (pill)  pill.classList.remove('visible');
      }
    }, 220);
  }

  apiDismissSuggestion(id).catch(() => {});
}

export async function handleProactiveSuggestionAction(action: any) {
  if (!action || !action.type) return;

  switch (action.type) {

    case 'trigger_briefing': {
      showChat();
      appendTypingIndicator();
      try {
        const data     = await apiGetTodayBriefing();
        const briefing = data.briefing;
        removeTypingIndicator();
        if (briefing) {
          appendMessage('bot', `**Daily Briefing**\n\n${briefing.message}`);
        } else {
          appendMessage('bot', "No briefing is available for today yet — check back after 6 AM or ask me directly: *\"What's on my plate today?\"*");
        }
      } catch (_) {
        removeTypingIndicator();
        appendMessage('bot', 'Could not load the daily briefing. Try asking me directly.');
      }
      break;
    }

    case 'prepare_meeting_brief': {
      showChat();
      const { matter_id, title } = action.payload || {};
      const prompt = matter_id
        ? `Prepare a briefing packet for my upcoming meeting: "${title || 'meeting'}"`
        : 'Prepare a briefing packet for my upcoming meeting';
      const input = dom.input;
      if (input) {
        input.value = prompt;
        input.dispatchEvent(new Event('input'));
      }
      setTimeout(() => handleSend(), 250);
      break;
    }

    case 'open_section': {
      const { section } = action.payload || {};
      if (section && typeof (window as any).showSection === 'function') {
        (window as any).showSection(section);
      }
      break;
    }

    case 'prefill_update': {
      showChat();
      const { matter_id } = action.payload || {};
      const input = dom.input;
      if (input) {
        input.value = matter_id
          ? `Draft a client update for matter ${matter_id}`
          : 'Draft a client update';
        input.dispatchEvent(new Event('input'));
        input.focus();
      }
      break;
    }

    default:
      console.warn('[AkiliBrain] Unknown suggestion action type:', action.type);
  }
}
